/**
 * Conditional-visibility rule builder (AR-328).
 *
 * Shared by the step and module panels. Rules read as sentences joined by a
 * single AND/OR toggle, which keeps a multi-condition rule scannable without a
 * nested-group editor.
 *
 * Fully controlled: the editor that renders it owns the draft, so nothing here
 * can drift from the panel it belongs to when a different step is selected.
 */
import React from 'react';
import { Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { VISIBILITY_RULE_FIELDS, type VisibilityRule } from '@/lib/activationBuilder';
import { PanelSectionLabel } from './activationPrimitives';

interface VisibilityRuleBuilderProps {
  rules: VisibilityRule[];
  join: 'AND' | 'OR';
  onJoinChange: (join: 'AND' | 'OR') => void;
  onRulesChange: (rules: VisibilityRule[]) => void;
  /** System steps show the rules read-only. */
  readOnly?: boolean;
}

const BLANK_RULE: VisibilityRule = { field: 'Route', operator: 'is one of', value: '' };

export const VisibilityRuleBuilder: React.FC<VisibilityRuleBuilderProps> = ({
  rules,
  join,
  onJoinChange,
  onRulesChange,
  readOnly = false,
}) => {
  const updateRule = (index: number, patch: Partial<VisibilityRule>) =>
    onRulesChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));

  return (
    <section>
      <PanelSectionLabel>Conditional visibility</PanelSectionLabel>
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/60">
        <div className="mb-2 flex items-center gap-2">
          <span className="text-[11px] text-gray-500 dark:text-gray-400">Show this when</span>
          <div className="inline-flex overflow-hidden rounded-md border border-gray-200 dark:border-gray-700">
            {(['AND', 'OR'] as const).map((option) => (
              <button
                key={option}
                type="button"
                disabled={readOnly}
                aria-pressed={join === option}
                onClick={() => onJoinChange(option)}
                className={cn(
                  'px-2 py-0.5 text-[10px] font-bold tracking-wider',
                  join === option
                    ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                    : 'bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400',
                  readOnly && 'cursor-not-allowed opacity-70',
                )}
              >
                {option}
              </button>
            ))}
          </div>
          <span className="text-[11px] text-gray-500 dark:text-gray-400">
            of the following match
          </span>
        </div>

        {rules.length ? (
          <ul className="space-y-1.5">
            {rules.map((rule, index) => (
              <li
                key={`${rule.field}-${index}`}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs dark:border-gray-700 dark:bg-gray-800"
              >
                {readOnly ? (
                  <>
                    <span className="font-semibold text-gray-700 dark:text-gray-200">
                      {rule.field}
                    </span>
                    <span className="text-gray-400">{rule.operator}</span>
                    <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-gray-600 dark:text-gray-300">
                      {rule.value}
                    </span>
                  </>
                ) : (
                  <>
                    <Select
                      value={rule.field}
                      onValueChange={(field) => updateRule(index, { field })}
                    >
                      <SelectTrigger className="h-7 w-40 text-[11px]" aria-label="Rule field">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {VISIBILITY_RULE_FIELDS.map((field) => (
                          <SelectItem key={field} value={field}>
                            {field}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-gray-400">{rule.operator}</span>
                    <Input
                      value={rule.value}
                      onChange={(event) => updateRule(index, { value: event.target.value })}
                      placeholder="Any"
                      aria-label={`${rule.field} value`}
                      className="h-7 min-w-0 flex-1 font-mono text-[11px]"
                    />
                    <button
                      type="button"
                      aria-label={`Remove ${rule.field} rule`}
                      onClick={() => onRulesChange(rules.filter((_, i) => i !== index))}
                      className="rounded p-0.5 text-gray-300 hover:text-gray-500 dark:text-gray-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[11px] text-gray-400 dark:text-gray-500">
            No conditions — this is shown to everyone in scope.
          </p>
        )}

        {readOnly ? (
          <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
            System steps are engineering-controlled — visibility can be viewed but not changed.
          </p>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRulesChange([...rules, BLANK_RULE])}
              className="mt-2 h-7 px-2 text-xs text-orange-600 dark:text-orange-400"
            >
              <Plus className="mr-1 h-3 w-3" /> Add condition
            </Button>
            <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
              Branch on {VISIBILITY_RULE_FIELDS.join(' · ')}.
            </p>
          </>
        )}
      </div>
    </section>
  );
};

export default VisibilityRuleBuilder;
